import PropTypes from 'prop-types';
import { useState, useEffect, useRef, useMemo } from 'react';
import CurrencyInput from 'react-currency-input-field';
import { LoadingButton } from '@mui/lab';
import debounce from 'lodash/debounce';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { enqueueSnackbar } from 'notistack';
import CheckIcon from '@mui/icons-material/Check';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Box, CircularProgress, Dialog, DialogContent, TextField, Typography, Autocomplete, Grid, Divider, Checkbox, Tooltip, InputAdornment, IconButton, } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import axiosInstance from '../../utils/axios';
import { objectToFormData } from '../../utils/objectToFormData';
import { epsilonRound } from '../../utils/math';
import InvoiceFiles from './InvoiceFiles';
import InvoiceItemTable from './InvoiceItemTable';
import CurrencyInputDefaults from '../../reuseable-components/CurrencyInput/CurrencyInputDefaults';
import updateItemInPlaceOrAppend from '../../utils/updateItemInPlaceOrAppend';

InvoiceForm.propTypes = {
  editInvoice: PropTypes.object,
  facilities: PropTypes.array,
  vendors: PropTypes.array,
  open: PropTypes.bool,
  close: PropTypes.func,
  setInvoices: PropTypes.func,
}

export default function InvoiceForm({ editInvoice, setInvoices, facilities, vendors, open, close }) {
  const gridRef = useRef(null);

  const [invoiceItems, setInvoiceItems] = useState([]);
  const [formValue, setFormValue] = useState({});
  const [invoiceExtraData, setInvoiceExtraData] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [activeFile, setActiveFile] = useState(null);
  const [files, setFiles] = useState([]);
  const [orderExists, setOrderExists] = useState(false);
  const [isLoadingOrderNumber, setIsLoadingOrderNumber] = useState(false);

  useEffect(() => {
    if (open && editInvoice) {
      setLoading(true);
      axiosInstance.get(`/invoice/invoiceData?id=${editInvoice.id}`)
        .then(res => {
          res.data.invoice.invoiceDate = new Date(res.data.invoice.invoiceDate);
          if (res.data.invoice.dueDate) res.data.invoice.dueDate = new Date(res.data.invoice.dueDate);
          if (res.data.invoice.tracking?.shippedDate) res.data.invoice.tracking.shippedDate = new Date(res.data.invoice.tracking.shippedDate);
          setInvoiceItems(res.data.items ?? []);
          setInvoiceExtraData({
            vendorBillLinkId: res.data.vendorBillURLId,
            customerInvoiceLinkId: res.data.customerInvoiceURLId,
            customerTotal: res.data.customerTotal
          });
          setFormValue(res.data.invoice);
          setFiles(res.data.files);
        })
        .catch(() => enqueueSnackbar('Invoice data fetch failed', { variant: 'error' }))
        .finally(() => setLoading(false));
    }
    else {
      setLoading(false);
      setInvoiceItems([]);
      setInvoiceExtraData({});
      setFiles([]);
      setActiveFile(null);
      setFormValue({ total: 0, shipping: 0 });
      setOrderExists(false);
    }
  }, [open]);

  const submit = (book, markAsBooked) => {
    setSubmitting(true);
    axiosInstance.post(`/invoice/invoiceForm`, objectToFormData({
      items: invoiceItems,
      invoice: formValue,
      files: files.filter(file => file.localFile).map(file => file.file),
      removeFiles: files.filter(file => file.delete).map(file => file.id),
      bookInvoice: book,
      forceThrough: formValue.forceThrough,
      updateBooking: book && formValue.booked && formValue.billed,
      markAsBooked: markAsBooked && !formValue.booked,
      markAsPending: markAsBooked && formValue.booked,
    }))
      .then((res) => {
        if (res.data) {
          res.data.facility = facilities.find(facility => facility.value === res.data.facilityId)?.label;
          res.data.vendor = vendors.find(vendor => vendor.value === res.data.vendorId)?.label;
          setInvoices(invoices => updateItemInPlaceOrAppend(invoices, res.data));
          enqueueSnackbar(editInvoice ? 'Invoice updated' : 'Invoice created', { variant: 'success' });
          if (book && res.data.error) enqueueSnackbar(res.data.error, { variant: 'error' });
          close();
        } else enqueueSnackbar(editInvoice ? 'Invoice update failed' : 'Invoice create failed', { variant: 'error' })
      })
      .catch(() => enqueueSnackbar(editInvoice ? 'Invoice update failed' : 'Invoice create failed', { variant: 'error' }))
      .finally(() => setSubmitting(false));
   }

  const debounceCheckOrder = useMemo(() => debounce((orderNumber, facilityId, open) => {
    if (!open || !orderNumber || !facilityId) { setOrderExists(false); return; }
    setIsLoadingOrderNumber(true)
    axiosInstance.get(encodeURI(`/invoice/checkOrderExist?orderNumber=${orderNumber}&facilityId=${facilityId}`))
      .then(res => setOrderExists(res.data === true))
      .catch(() => enqueueSnackbar('Failed to check order number', { variant: 'error' }))
      .finally(() => setIsLoadingOrderNumber(false));
  }, 300), []);

  useEffect(() => {
    debounceCheckOrder(formValue.orderNumber, formValue.facilityId, open);
  }, [formValue.orderNumber, formValue.facilityId]);

  const parsedTotal = Number(formValue?.total ?? 0);
  const parsedShipping = Number(formValue?.shipping ?? 0);
  const parsedTariffSurcharge = Number(formValue?.tariffSurcharge ?? 0);

  const itemTotal = epsilonRound(invoiceItems.reduce((acc, item) => acc + (item.price * item.quantity) + item.taxAmount, 0) + parsedShipping + parsedTariffSurcharge);
  const customerTotal = epsilonRound(invoiceItems.reduce((acc, item) => acc + ((item.customerPrice ?? 0) * item.quantity) + item.taxAmount, 0) + parsedShipping + parsedTariffSurcharge);
  const customerTotalError = customerTotal < parsedTotal * 1.05;

  const pdfFile = useMemo(() => activeFile ? <Grid item xl container spacing={2} >
    <Divider orientation="vertical" sx={{ mx: 2 }} />
    <Grid item xl container spacing={2}>
      <object data={activeFile.localFile ? URL.createObjectURL(activeFile.file) : `/api/invoice/invoiceAttachment?id=${activeFile.id}`} type="application/pdf"
        width="100%" height="100%">
        <p>Failed to load PDF</p>
      </object>
    </Grid>
  </Grid > : "", [activeFile]);

  return <Dialog open={open} onClose={close} maxWidth={activeFile ? null : "xl"} >
    <DialogContent>
      {!loading ? <Grid container spacing={2}>
        <Grid item container xl={activeFile ? 6 : 12} spacing={2}>
          <Grid item xl={12} container spacing={2}>
            <Grid item xl={formValue.booked ? 3 : 6}>
              <Typography variant="subtitle1">Vendor Invoice Id*</Typography>
              <TextField
                name="vendorInvoiceId"
                size="small"
                placeholder="Enter Vendor Invoice Id"
                fullWidth
                value={formValue.vendorInvoiceId ?? ""}
                onChange={(e) => setFormValue(form => ({ ...form, vendorInvoiceId: e.target.value }))}
                InputProps={invoiceExtraData.vendorBillLinkId ? {
                  endAdornment: <InputAdornment position="end">
                    <IconButton onClick={() => window.open(`https://qbo.intuit.com/app/bill?txnId=${invoiceExtraData.vendorBillLinkId}`, "_blank")} disableRipple>
                      <OpenInNewIcon />
                    </IconButton>
                  </InputAdornment>
                } : {}}
              />
            </Grid>
            {formValue.booked && <Grid item xl={3}>
              <Typography variant="subtitle1">Customer Invoice Id</Typography>
              <TextField
                name="quickbooksInvoiceId"
                size="small"
                placeholder="Enter Customer Invoice Id"
                fullWidth
                value={formValue.quickbooksInvoiceId ?? ""}
                onChange={(e) => setFormValue(form => ({ ...form, quickbooksInvoiceId: e.target.value }))}
                InputProps={invoiceExtraData.customerInvoiceLinkId ? {
                  endAdornment: <InputAdornment position="end">
                    <IconButton onClick={() => window.open(`https://qbo.intuit.com/app/invoice?txnId=${invoiceExtraData.customerInvoiceLinkId}`, "_blank")} disableRipple>
                      <OpenInNewIcon />
                    </IconButton>
                  </InputAdornment>
                } : {}}
              />
            </Grid>}
            <Grid item xl={6}>
              <Typography variant="subtitle1">Order Number*</Typography>
              <TextField
                name="orderNumber"
                size="small"
                placeholder="Enter Order Number"
                fullWidth
                value={formValue.orderNumber ?? ""}
                onChange={(e) => setFormValue(form => ({ ...form, orderNumber: e.target.value }))}
                InputProps={{
                  endAdornment: (<InputAdornment position="end">
                    {isLoadingOrderNumber ? <Tooltip title="Checking order number..." arrow placement="top"><CircularProgress size={16} /></Tooltip>
                      : orderExists ? <Tooltip title="Order exists" arrow placement="top"><CheckIcon fontSize="small" color="success" /></Tooltip>
                        : !orderExists && formValue.orderNumber && formValue.facilityId ? <Tooltip title="Order not found" arrow placement="top"><HelpOutlineIcon fontSize="small" color="info" /></Tooltip>
                          : null}
                  </InputAdornment>),
                }}
              />
            </Grid>
          </Grid>
          <Grid item xl={12} container spacing={2}>
            <Grid item xl={6}>
              <Typography variant="subtitle1">Facility*</Typography>
              <Autocomplete
                fullWidth
                size="small"
                value={facilities.find(facility => facility.value === formValue.facilityId) ?? null}
                onChange={(event, val) => setFormValue(form => ({ ...form, facilityId: val?.value ?? 0 }))}
                options={facilities}
                renderInput={(params) => <TextField {...params} placeholder="Facility" />}
              />
            </Grid>
            <Grid item xl={6}>
              <Typography variant="subtitle1">Vendor*</Typography>
              <Autocomplete
                fullWidth
                size="small"
                value={vendors.find(vendor => vendor.value === formValue.vendorId) ?? null}
                onChange={(event, val) => setFormValue(form => ({ ...form, vendorId: val?.value ?? 0 }))}
                options={vendors}
                renderInput={(params) => <TextField {...params} placeholder="Vendor" />}
              />
            </Grid>
          </Grid>
          <Grid item xl={12} container spacing={2}>
            <Grid item xl={6}>
              <Typography variant="subtitle1">Invoice Date*</Typography>
              <DatePicker
                placeholder="Invoice Date"
                sx={{ width: "100%", "& input": { height: "8px" } }}
                size="small"
                fullWidth
                value={formValue.invoiceDate ?? null}
                onChange={(newValue) => setFormValue(form => ({ ...form, invoiceDate: newValue }))}
              />
            </Grid>
            <Grid item xl={6}>
              <Typography variant="subtitle1">Due Date</Typography>
              <DatePicker
                sx={{ width: "100%", "& input": { height: "8px" } }}
                placeholder="Due Date"
                size="small"
                fullWidth
                value={formValue.dueDate ?? null}
                onChange={(newValue) => setFormValue(form => ({ ...form, dueDate: newValue }))}
              />
            </Grid>
          </Grid>
          <Grid item xl={12} container spacing={2}>
            <Grid item xl={invoiceExtraData.customerTotal > 0 ? 2 : 4}>
              <Typography variant="subtitle1">Total</Typography>
              <CurrencyInput
                {...CurrencyInputDefaults()}
                name="total"
                placeholder="Enter Total"
                error={itemTotal !== parsedTotal || customerTotalError}
                value={formValue.total ?? ""}
                helperText={itemTotal !== parsedTotal ? `Items total plus shipping doesn't equal the total. Current item total ${itemTotal
                  .toLocaleString('en-US', { style: 'currency', currency: 'USD' })}` :
                  customerTotalError ? `Customer price total does not reach the required profit margins. Current item total: ${customerTotal
                    .toLocaleString('en-US', { style: 'currency', currency: 'USD' })}.
                  Minimum: ${(parsedTotal * 1.05).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}` : ""}
                onValueChange={(value) => setFormValue(form => ({ ...form, total: value }))}
              />
            </Grid>
            {invoiceExtraData.customerTotal > 0 && <Grid item xl={2}>
              <Tooltip arrow title="Customer total based on last invoice sent to customer">
                <Box>
                  <Typography variant="subtitle1">Customer Total</Typography>
                  <CurrencyInput {...CurrencyInputDefaults()} value={invoiceExtraData.customerTotal} disabled />
                </Box>
              </Tooltip>
            </Grid>}
            <Grid item xl={4}>
              <Typography variant="subtitle1">Shipping</Typography>
              <CurrencyInput
                {...CurrencyInputDefaults()}
                name="shipping"
                placeholder="Enter Shipping"
                value={formValue.shipping}
                onValueChange={(value) => setFormValue(form => ({ ...form, shipping: value }))}
              />
            </Grid>
            <Grid item xl={4}>
              <Typography variant="subtitle1">Tariff Surcharge</Typography>
              <CurrencyInput
                {...CurrencyInputDefaults()}
                name="tariffSurcharge"
                placeholder="Enter Tariff Surcharge"
                value={formValue.tariffSurcharge}
                onValueChange={(value) => setFormValue(form => ({ ...form, tariffSurcharge: value }))}
              />
            </Grid>
          </Grid>
          {(formValue.isManual || !editInvoice) && (<Grid item xl={12} container spacing={2}>
            <Grid item xl={4}>
              <Typography variant="subtitle1">Tracking Number</Typography>
              <Tooltip title={!orderExists ? "Valid order number and facility required" : ""} arrow placement="top">
                <TextField
                  name="trackingNumber"
                  size="small"
                  placeholder="Enter Tracking Number"
                  fullWidth
                  disabled={!orderExists}
                  value={formValue.tracking?.trackingNumber ?? ""}
                  onChange={(e) => setFormValue(form => ({ ...form, tracking: { ...form.tracking, trackingNumber: e.target.value } }))}
                />
              </Tooltip>
            </Grid>
            <Grid item xl={4}>
              <Typography variant="subtitle1">Shipped Date</Typography>
              <Tooltip title={!orderExists ? "Valid order number and facility required" : ""} arrow placement="top">
                <Box>
                  <DatePicker
                    placeholder="Shipped Date"
                    sx={{ width: "100%", "& input": { height: "8px" } }}
                    size="small"
                    fullWidth
                    disabled={!orderExists}
                    value={formValue.tracking?.shippedDate ?? null}
                    onChange={(value) => setFormValue(form => ({ ...form, tracking: { ...form.tracking, shippedDate: value } }))}
                  />
                </Box>
              </Tooltip>
            </Grid>
            <Grid item xl={4}>
              <Typography variant="subtitle1">Tracking Carrier</Typography>
              <Tooltip title={!orderExists ? "Valid order number and facility required" : ""} arrow placement="top">
                <TextField
                  name="trackingCarrier"
                  size="small"
                  placeholder="Enter Tracking Carrier"
                  fullWidth
                  disabled={!orderExists}
                  value={formValue.tracking?.trackingCarrier ?? ""}
                  onChange={(e) => setFormValue(form => ({ ...form, tracking: { ...form.tracking, trackingCarrier: e.target.value } }))}
                />
              </Tooltip>
            </Grid>
          </Grid>)}

          <Grid item xl={12}>
            <InvoiceItemTable items={invoiceItems} formData={formValue} setItems={setInvoiceItems} gridRef={gridRef} />
          </Grid>
          <Grid item xl={12} container spacing={2}>
            <Grid item xl={12}>
              <InvoiceFiles activeFile={activeFile} files={files} setFiles={setFiles} setActiveFile={setActiveFile} />
            </Grid>
          </Grid>
          <Grid item xl={12}>
            <Box> <Tooltip arrow title="When booking the invoice ignores item errors like price or quantity issues and pushes the invoice through anyway.">
              <Checkbox checked={formValue.forceThrough ?? false} onChange={(e) => setFormValue(value => ({ ...value, forceThrough: e.target.checked }))} />
            </Tooltip>
              Ignore item issues
            </Box>
          </Grid>
          <Grid item xl={12}>
            <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
              <LoadingButton loading={submitting} disabled={!formValue?.vendorInvoiceId || !formValue?.orderNumber || !formValue?.vendorId ||
                !formValue?.facilityId || !formValue?.invoiceDate}
                sx={{ px: 7, textDecoration: "underline" }} color="purple" size="medium" variant="text" onClick={() => submit(false, false)}>
                Save as draft
              </LoadingButton>
              <LoadingButton loading={submitting} disabled={!formValue?.vendorInvoiceId || !formValue?.orderNumber || !formValue?.vendorId ||
                !formValue?.facilityId || !formValue?.invoiceDate}
                sx={{ px: 7, ml: 2 }} color="purple" size="medium" variant="outlined" onClick={() => submit(false, true)}>
                {formValue.booked ? "Save as pending" : "Save as booked"}
              </LoadingButton>
              <LoadingButton loading={submitting} disabled={!formValue?.vendorInvoiceId || !formValue?.orderNumber || !formValue?.vendorId ||
                !formValue?.facilityId || !formValue?.invoiceDate || !invoiceItems.length}
                sx={{ px: 7, ml: 2 }} color="purple" size="medium" variant="contained" onClick={() => submit(true, false)}>
                {formValue.booked ? "Save & Update Book" : "Save & Book"}
              </LoadingButton>
            </Box>
          </Grid>
        </Grid>
        {pdfFile}
      </Grid> : <Box sx={{ display: "flex", justifyContent: "center", m: 3 }}><CircularProgress /></Box>}
    </DialogContent>
  </Dialog>;
}